import { useNavigate } from 'react-router-dom'
import { faPlain, money, toman } from 'utilities/helper/format'
import {
    cashboxIntervalLabel,
    cashboxStateChip,
    cashboxStateLabel,
} from 'utilities/helper/cashBoxes'

interface Props {
    cashbox: ICashboxDetail
    /**
     * Where a tap goes, when it should not go to the router.
     *
     * The قرعه‌کشی landing renders this header over a fixture box, where
     * «پرداخت سهم» must open the sign-up call to action instead of a payment
     * screen for a صندوق that does not exist. Unset everywhere else, which is
     * the ordinary in-app navigation.
     */
    onNavigate?: (to: string) => void
}

/**
 * The صندوق summary: what the box holds, what it still needs, and when the
 * next draw is.
 *
 * `satisfactory_amount` (= total_shares × share_value) is what the box must
 * hold before a draw is allowed — `Cashbox.is_financially_satisfied` compares
 * `balance` to it with `==`, so we show the gap explicitly. A member who only
 * sees "موجودی" cannot tell why the draw button is dead.
 */
export default function CashboxHeader({ cashbox, onNavigate }: Props) {
    const navigate = useNavigate()
    const go = onNavigate ?? navigate
    const period = cashbox.period
    const chip = cashboxStateChip(cashbox.state)
    const remaining = Math.max(
        0,
        (period?.satisfactory_amount ?? 0) - (cashbox.balance ?? 0)
    )
    const myShare = cashbox.membership
    const isOwner = myShare?.role === 'own'

    return (
        <div className="bg-white">
            <div className="px-4 pt-2 pb-4">
                <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                        <h1 className="text-lg font-bold text-black truncate">
                            صندوق {cashbox.name}
                        </h1>
                        <p className="text-sm text-text-secondary mt-1">
                            {faPlain.format(period?.valid_memberships_count ?? 0)}{' '}
                            عضو ·{' '}
                            {cashboxIntervalLabel(
                                period?.interval,
                                period?.day_of_draw
                            )}
                        </p>
                    </div>
                    <div
                        className="inline-flex items-center rounded-[6px] px-2.5 py-1 shrink-0"
                        style={{ backgroundColor: chip.bgColor }}
                    >
                        <span
                            className="text-xs font-bold"
                            style={{ color: chip.color }}
                        >
                            {cashboxStateLabel(cashbox.state, period)}
                        </span>
                    </div>
                </div>

                <div className="mt-4 rounded-card border border-divider p-3">
                    <div className="flex justify-between items-baseline">
                        <span className="text-sm text-text-secondary">
                            موجودی صندوق
                        </span>
                        <span className="text-base font-bold text-black">
                            {toman(cashbox.balance)}
                        </span>
                    </div>
                    <div className="flex justify-between items-baseline mt-2">
                        <span className="text-sm text-text-secondary">
                            مبلغ لازم برای قرعه‌کشی
                        </span>
                        <span className="text-sm text-black">
                            {toman(period?.satisfactory_amount ?? 0)}
                        </span>
                    </div>
                    {remaining > 0 && (
                        <div className="flex justify-between items-baseline mt-2">
                            <span className="text-sm text-text-secondary">
                                باقی‌مانده تا قرعه‌کشی
                            </span>
                            <span className="text-sm font-bold text-[#E0564C]">
                                {toman(remaining)}
                            </span>
                        </div>
                    )}
                    {cashbox.inprogress_balance > 0 && (
                        <div className="flex justify-between items-baseline mt-2">
                            <span className="text-sm text-text-secondary">
                                در حال انتقال به بانک
                            </span>
                            <span className="text-sm text-black">
                                {toman(cashbox.inprogress_balance)}
                            </span>
                        </div>
                    )}
                </div>

                <div className="mt-3 grid grid-cols-2 gap-3">
                    <Stat
                        label="ارزش هر سهم"
                        value={toman(period?.share_value ?? 0)}
                    />
                    <Stat
                        label="مبلغ هر وام"
                        value={toman(period?.loan_amount ?? 0)}
                    />
                    <Stat
                        label="تعداد سهم"
                        value={`${faPlain.format(period?.total_shares ?? 0)} سهم`}
                    />
                    <Stat
                        label="تعداد وام در هر نوبت"
                        value={`${faPlain.format(period?.number_of_loans ?? 0)} وام`}
                    />
                    <Stat
                        label="نوبت جاری"
                        value={
                            period?.cycle_index
                                ? `نوبت ${faPlain.format(period.cycle_index)}`
                                : '—'
                        }
                    />
                    <Stat
                        label="تاریخ قرعه‌کشی"
                        value={cashbox.cycle?.draw_date_jalali || '—'}
                    />
                </div>

                {/* An `unp` box looks normal in every other field, so say
                    plainly that it is not running yet and why. */}
                {cashbox.state === 'unp' && (
                    <div className="mt-3 rounded-card border border-[#F0C36D] bg-[#FFF8E6] p-3">
                        <p className="text-sm font-bold text-[#8A6100]">
                            نسخه‌ی صندوق مشخص نشده
                        </p>
                        <p className="text-xs text-[#8A6100] mt-1 leading-5">
                            تا انتخاب نسخه‌ی کارمزدی یا پرداخت اشتراک، این
                            صندوق فعال نمی‌شود.
                        </p>
                    </div>
                )}

                {cashbox.state !== 'unp' && (
                    <div className="mt-3 flex justify-between items-baseline">
                        <span className="text-xs text-text-secondary">
                            نسخه‌ی صندوق
                        </span>
                        <span className="text-xs text-black">
                            {cashbox.plan === 'sub'
                                ? typeof cashbox.service_package_remaining_days ===
                                      'number' &&
                                  cashbox.service_package_remaining_days > 0
                                    ? `اشتراکی · ${faPlain.format(
                                          cashbox.service_package_remaining_days
                                      )} روز باقی‌مانده`
                                    : 'اشتراکی · منقضی شده'
                                : 'کارمزدی'}
                        </span>
                    </div>
                )}

                {myShare && (
                    <div className="mt-4 rounded-card bg-[#FAFAFA] border border-divider p-3">
                        <p className="text-sm font-bold text-black">سهم شما</p>
                        <div className="flex justify-between items-baseline mt-2">
                            <span className="text-sm text-text-secondary">
                                پرداخت‌شده در این نوبت
                            </span>
                            <span className="text-sm text-black">
                                {toman(myShare.balance)}
                            </span>
                        </div>
                        <div className="flex justify-between items-baseline mt-2">
                            <span className="text-sm text-text-secondary">
                                باقی‌مانده سهم شما
                            </span>
                            <span className="text-sm font-bold text-black">
                                {toman(myShare.remaining_share_amount)}
                            </span>
                        </div>
                        {myShare.remaining_share_amount > 0 &&
                            cashbox.state === 'act' && (
                                <button
                                    type="button"
                                    className="mt-3 w-full h-11 rounded-[10px] bg-green-1 text-white text-sm font-bold active:scale-[0.99]"
                                    onClick={() =>
                                        go(
                                            `/easy-pay?select=all&box=${cashbox.id}`
                                        )
                                    }
                                >
                                    پرداخت سهم ({money(
                                        myShare.remaining_share_amount
                                    )}{' '}
                                    تومان)
                                </button>
                            )}
                    </div>
                )}

                {isOwner && cashbox.last_cycle_unpaid_winners?.length > 0 && (
                    <div className="mt-3 rounded-card border border-[#F0C36D] bg-[#FFF8E6] p-3">
                        <p className="text-sm font-bold text-[#8A6100]">
                            پرداخت وام برنده
                        </p>
                        <p className="text-xs text-[#8A6100] mt-1 leading-5">
                            {faPlain.format(
                                cashbox.last_cycle_unpaid_winners.length
                            )}{' '}
                            برنده هنوز وام خود را دریافت نکرده‌اند. پرداخت وام
                            برندگان فعلاً از طریق اپلیکیشن اندروید انجام می‌شود.
                        </p>
                    </div>
                )}
            </div>
        </div>
    )
}

function Stat({ label, value }: { label: string; value: string }) {
    return (
        <div className="rounded-card border border-divider p-2.5">
            <p className="text-xs text-text-secondary">{label}</p>
            <p className="text-sm font-bold text-black mt-1">{value}</p>
        </div>
    )
}
